import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import type { AIDecorationExperiencePluginState } from '../decoration/reducer';
import { createTelepointerWidget } from '../in-editor-direct-streaming/decorations';

import { purePmInEditorDirectStreamingPluginKey as streamingPluginKey } from './pluginKey';

const userContentDecorationAttrs = {
	class: 'ai-in-editor-user-content',
	'aria-hidden': 'true',
	'data-testid': 'ai-in-editor-user-content',
};

/**
 * Builds the decoration set for the streaming plugin.
 *
 * This creates decorations for:
 * - User content highlight (loading state)
 * - AI content highlight (streaming state)
 * - Telepointer widget (cursor indicator during streaming)
 *
 * @param state - Current editor state
 * @param telepointerElement - The telepointer DOM element to use for the widget
 * @param preloadedDecorationState - Optional pre-fetched aiExperienceDecoration plugin state.
 *   Pass this from the caller when it already has the state to avoid a redundant getState call
 *   in this hot path.
 * @returns DecorationSet containing all active decorations
 */
export function buildStreamingDecorations(
	state: EditorState,
	telepointerElement: HTMLElement,
	preloadedDecorationState?: AIDecorationExperiencePluginState,
): DecorationSet {
	const pluginState = streamingPluginKey.getState(state);
	if (!pluginState) {
		return DecorationSet.empty;
	}

	// Use the pre-fetched decoration state if provided (avoiding a redundant getState call
	// when the caller already has it), otherwise fetch it here.
	const decorationState =
		preloadedDecorationState !== undefined
			? preloadedDecorationState
			: aiExperienceDecorationPluginKey.getState(state);

	const {
		showHighlight,
		showAIContentHighlight,
		showTelepointer,
		startPos,
		userContentEndPos,
		aiContentEndPos,
		telepointerPos,
		loadingPos,
		isFinalStream,
		isStreaming,
	} = pluginState;

	const decorations: Decoration[] = [];

	// When not actively streaming, show telepointer if there's an active session with positions set
	if (!isStreaming || startPos === undefined) {
		const { nextSessionId, positions } = decorationState || {};

		if (nextSessionId && positions) {
			return DecorationSet.create(state.doc, [
				createTelepointerWidget(positions[0], telepointerElement),
			]);
		}

		return DecorationSet.empty;
	}

	// User content highlight (loading state)
	if (showHighlight) {
		if (loadingPos !== undefined) {
			decorations.push(Decoration.inline(startPos, loadingPos, userContentDecorationAttrs));
		} else {
			if (userContentEndPos !== undefined) {
				decorations.push(
					Decoration.inline(
						aiContentEndPos || startPos,
						userContentEndPos,
						userContentDecorationAttrs,
					),
				);
			}
		}

		// AI content highlight (streaming state)
		if (showAIContentHighlight && aiContentEndPos !== undefined) {
			decorations.push(
				Decoration.inline(startPos, aiContentEndPos, {
					class: 'ai-in-editor-ai-content',
					'data-testid': 'ai-in-editor-ai-content',
				}),
			);
		}
	}

	/**
	 * We show the telepointer only when isFinalStream is false.
	 * On the final stream, plugin state is reset so isFinalStream will be undefined.
	 */
	if (isFinalStream === false && showTelepointer) {
		decorations.push(
			createTelepointerWidget(telepointerPos || aiContentEndPos || startPos, telepointerElement),
		);
	} else {
		// Between streams: show telepointer if session is active
		const { nextSessionId } = decorationState || {};
		if (nextSessionId && telepointerPos !== undefined) {
			decorations.push(createTelepointerWidget(telepointerPos, telepointerElement));
		}
	}

	if (decorations.length) {
		return DecorationSet.create(state.doc, decorations);
	}
	return DecorationSet.empty;
}
